/* eslint-disable @next/next/no-img-element */
import React, { useContext, useEffect, useState } from "react";
import Input from "@/designs/Input";
import Modal from "@/designs/Modal";
import Button from "@/designs/Button";
import {
  GetVistorsDocument,
  useGetAgreementLazyQuery,
  useGetFieldQuery,
  useGetOfficeLocationQuery,
  useUpdateVisitorMutation,
  Visitor,
} from "@/graphql/generated/schema";
import moment from "moment";
import Loader from "@/designs/Loader";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { ChevronDown, Download, MoveDown, MoveUp } from "lucide-react";
import { jsPDF } from "jspdf";
import { CMSModal } from "@/context";
import { twMerge } from "tailwind-merge";
import VerticalNavigator from "@/designs/VerticalNavigator";
import VisitorStatusSelect from "../VisitorStatusSelect";
import { Divider } from "antd";
import { useTranslation } from "react-i18next";

interface EditTimelineProp {
  isOpen: boolean;
  onClose: () => void;
  timeline: Visitor;
  allvisitors: Visitor[] | any;
  setTimeline: (value: Visitor) => void;
}

const EditTimeline = ({
  isOpen,
  onClose,
  timeline,
  allvisitors,
  setTimeline,
}: EditTimelineProp) => {
  const { user, theme } = useContext(CMSModal);
  const { t } = useTranslation();
  const [formData, setFormData] = useState<Record<string, any>>({});

  const [signedType, setSignedType] = useState<string>(
    timeline?.signedType || "",
  );
  const [isChanged, setIsChanged] = useState(false);
  const [notes, setNotes] = useState("");

  const { data: fields } = useGetFieldQuery({
    variables: {
      categoryId: timeline?.category?.id,
    },
  });
  const fieldsData = fields?.getField;

  const [updateVisitor, { loading }] = useUpdateVisitorMutation({
    refetchQueries: [GetVistorsDocument],
  });

  const [getAgreement, { loading: agreementLoading }] =
    useGetAgreementLazyQuery();

  const { data: locationData } = useGetOfficeLocationQuery({
    variables: { locationId: timeline?.location?.id ?? "" },
    skip: !timeline?.location?.id,
  });

  const agreementId =
    timeline?.selectedAgreement?.agreement ||
    locationData?.getOfficeLocation?.selectedAgreement?.agreement ||
    null;

  // Initialize form when modal opens or timeline changes
  useEffect(() => {
    if (timeline) {
      setFormData({
        ...(timeline.data || {}),
        department: timeline.department?.id || "",
        employee:
          (timeline.employees &&
            `${timeline?.employees[0]?.firstName} ${timeline?.employees[0]?.lastName}`) ||
          "",
      });
      setSignedType(timeline.signedType || "");
      setNotes("");
      setIsChanged(false);
    }
  }, [timeline, isOpen]);

  const handleInputChange = (key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setIsChanged(true);
  };

  const handleSelectChange = (value: string) => {
    setSignedType(value);
    setIsChanged(true);
  };

  const handleSave = async () => {
    try {
      const input = {
        _id: timeline?.id,
        signedType: signedType,
        img: timeline?.img || null,
        department: formData?.department || null,
        category: timeline?.category?.id || null,
        data: formData,
        note: notes,
      };

      await updateVisitor({
        variables: { input },
      });

      setIsChanged(false);
      onClose(); // optionally close modal after saving
    } catch (error) {
      console.error("Failed to update visitor:", error);
    }
  };
  const start = moment(timeline?.signedIn);
  const end = moment(timeline?.signedOut);
  const diffMinutes = end.diff(start, "minutes"); // difference in minutes
  const diffSeconds = end.diff(start, "seconds"); // if you prefer seconds

  // You can format it nicely:
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;
  const currentIndex = allvisitors?.findIndex(
    (v: Visitor) => v.id === timeline?.id,
  );

  const handleMove = (direction: "up" | "down") => {
    if (!Array.isArray(allvisitors)) return;

    let newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= allvisitors.length) return; // out of bounds

    const nextVisitor = allvisitors[newIndex];
    if (nextVisitor) setTimeline(nextVisitor);
  };

  const handleDownloadAgreement = async (agreementId: string) => {
    try {
      const { data } = await getAgreement({
        variables: { agreementId },
      });

      const agreementData = data?.getAgreement;
      if (!agreementData) return;

      const fullName = timeline.data.fullName;
      const companyName = timeline.data.company;
      const phone = timeline.data.phone;
      const visiting: any =
        timeline.employees && timeline.employees.length > 0
          ? `${timeline?.employees[0]?.firstName} ${timeline?.employees[0]?.lastName}`
          : timeline.department?.name;
      const date = timeline.signedIn;

      const signatureImg = timeline.selectedAgreement?.signatureImg;

      // Create hidden container for HTML rendering
      const container = document.createElement("div");
      container.style.width = "800px";
      container.style.padding = "40px";
      container.style.fontFamily = "Arial";
      container.innerHTML = `
      <h1 style="text-align:center;font-size:22px; font-weight:bold;">${user.company.name}</h1>
      ${agreementData.content}
    `;

      document.body.appendChild(container);

      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      await pdf.html(container, {
        x: 0,
        y: 0,
        width: pageWidth,
        windowWidth: 800,
        callback: function (pdf) {
          const footerY = pageHeight - 40;

          pdf.setFontSize(11);

          let footerLineY = footerY;

          const drawLabelValue = (label: string, value?: string) => {
            if (!value) return;

            const x = 20;

            // Bold label
            pdf.setFont("helvetica", "normal");
            pdf.text(`${label}:`, x, footerLineY);

            const labelWidth = pdf.getTextWidth(`${label}: `);

            // Normal value
            pdf.setFont("helvetica", "bold");
            pdf.text(value, x + labelWidth, footerLineY);

            footerLineY += 6;
          };

          // Footer fields
          drawLabelValue("Name", fullName);
          drawLabelValue("Company Name", companyName);
          drawLabelValue("Phone", phone);
          drawLabelValue("Visiting", visiting);
          drawLabelValue("Date", moment(date).format("MMM D, YYYY, h:mma"));

          // Signature
          if (signatureImg) {
            const imgWidth = 50;
            const imgHeight = 20;

            pdf.addImage(
              signatureImg,
              "PNG",
              pageWidth - imgWidth - 20,
              footerY - 15,
              imgWidth,
              imgHeight,
            );

            pdf.text("Signature", pageWidth - imgWidth - 20, footerY + 8);
          }

          const formattedDate = moment(date).format("YYYY-MM-DD");
          const fileName = `${fullName.replace(/\s+/g, "_")}_Agreement_${formattedDate}.pdf`;
          // Create PDF blob
          const pdfBlob = pdf.output("blob");

          // Open in new tab
          const blobUrl = URL.createObjectURL(pdfBlob);
          window.open(blobUrl, "_blank");

          // Trigger download with filename
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Cleanup URL after a bit
          setTimeout(() => {
            URL.revokeObjectURL(blobUrl);
          }, 1000);

          // Cleanup HTML container
          document.body.removeChild(container);
        },
      });
    } catch (err) {
      console.error("PDF generation failed", err);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} disableBackdropClose={isChanged}>
      <VerticalNavigator
        currentIndex={currentIndex}
        total={allvisitors.length}
        onMoveUp={() => handleMove("up")}
        onMoveDown={() => handleMove("down")}
        theme={theme}
      />

      {(loading || agreementLoading) && <Loader />}
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex items-center gap-5">
          {timeline?.img ? (
            <img
              src={timeline?.img || "/placeholder-user.jpg"}
              alt="Visitor"
              className={twMerge(
                "w-20 h-20 rounded-full border object-cover transition-colors",
                theme === "dark" ? "border-gray-800" : "border-gray-200",
              )}
            />
          ) : (
            <img
              src="/Avatar.svg"
              alt="Visitor"
              className={twMerge(
                "w-20 h-20 rounded-full border object-cover transition-colors",
                theme === "dark" ? "border-gray-800" : "border-gray-200",
              )}
            />
          )}
          <div className="space-y-1 flex-1 transition-colors">
            <h2
              className={twMerge(
                "text-lg font-bold transition-colors",
                theme === "dark" ? "text-gray-100" : "",
              )}
            >
              {formData?.fullName || t("visitors.editTimeline.unnamedVisitor")}
            </h2>
            <p
              className={twMerge(
                "text-sm transition-colors",
                theme === "dark" ? "text-gray-400" : "text-gray-600",
              )}
            >
              {signedType
                ? `${t("visitors.editTimeline.signedInAt")} ${moment(
                    timeline?.signedIn,
                  ).format("h:mma, MMM Do")}`
                : `${t("visitors.editTimeline.signedOutAt")} ${moment(
                    timeline?.signedOut,
                  ).format("h:mma, MMM Do")}`}
            </p>

            <div className="relative top-4">
              <VisitorStatusSelect
                value={signedType}
                onChange={(newStatus) => handleSelectChange(newStatus)}
              />
            </div>
          </div>
        </div>

        <Divider />
        {/* Timeline Info */}
        <div className="space-y-3">
          <h2
            className={twMerge(
              "font-semibold transition-colors",
              theme === "dark" ? "text-gray-100" : "text-black",
            )}
          >
            {t("visitors.editTimeline.inAndOut")}
          </h2>
          <div
            className={twMerge(
              "grid grid-cols-2 text-sm transition-colors",
              theme === "dark" ? "text-gray-300" : "",
            )}
          >
            <span className="text-gray-500">
              {t("visitors.editTimeline.signedIn")}
            </span>
            <div className="flex flex-col justify-center gap-1">
              <p
                className={twMerge(
                  "transition-colors font-medium",
                  theme === "dark" ? "text-gray-100" : "",
                )}
              >
                {moment(timeline?.signedIn).format(" h:mma, MMM Do")}
              </p>
              <span className="text-xs text-gray-500">
                {[
                  timeline?.location?.name,
                  timeline?.signedInDevice === "Web"
                    ? t("visitors.editTimeline.webDashboard")
                    : t("visitors.editTimeline.Ios"),
                ]
                  .filter(Boolean)
                  .join(" on the ")}
              </span>
            </div>
          </div>
          <div
            className={twMerge(
              "grid grid-cols-2 text-sm transition-colors",
              theme === "dark" ? "text-gray-300" : "",
            )}
          >
            <span className="text-gray-500">
              {t("visitors.editTimeline.signedOut")}
            </span>
            <div className="flex flex-col justify-center gap-1">
              <p
                className={twMerge(
                  "transition-colors font-medium",
                  theme === "dark" ? "text-gray-100" : "",
                )}
              >
                {timeline?.signedOut
                  ? moment(timeline?.signedOut).format(" h:mma, MMM Do")
                  : "-"}
              </p>
              {timeline?.signedOut && (
                <span className="text-xs text-gray-500">
                  {[
                    timeline?.location?.name,
                    timeline?.signedOutDevice === "Web"
                      ? t("visitors.editTimeline.webDashboard")
                      : t("visitors.editTimeline.Ios"),
                  ]
                    .filter(Boolean)
                    .join(" on the ")}
                </span>
              )}
            </div>
          </div>
          <div
            className={twMerge(
              "grid grid-cols-2 text-sm transition-colors",
              theme === "dark" ? "text-gray-300" : "",
            )}
          >
            <span className="text-gray-500">
              {t("visitors.editTimeline.totalTime")}
            </span>
            <p
              className={twMerge(
                "transition-colors font-medium",
                theme === "dark" ? "text-gray-100" : "",
              )}
            >
              {timeline?.signedOut
                ? hours > 0
                  ? `${hours}h ${minutes}m`
                  : `${minutes}m (${diffSeconds}s)`
                : "-"}
            </p>
          </div>
        </div>

        <Divider />
        {/* Visitor Details */}
        <div className="space-y-3">
          {/* Fixed fields at top */}
          <Input
            label={t("visitors.editTimeline.fullName")}
            value={formData?.fullName || ""}
            onChange={(value) => handleInputChange("fullName", value)}
            required
          />

          <Input
            label={t("visitors.editTimeline.visitorType")}
            value={timeline?.category?.name || ""}
            readonly
          />

          {/* Dynamically render fields from fieldsData */}
          {fieldsData &&
            fieldsData.length > 0 &&
            fieldsData
              ?.filter((f: any) => f.enabled && f.name !== "fullName") // exclude fullName already shown above
              .map((field: any) => {
                const value = formData[field.name] || "";

                if (field.type === "text" || field.type === "date") {
                  return (
                    <Input
                      key={field.id}
                      type={field.type}
                      label={field.label}
                      value={value || ""}
                      required={field.required}
                      onChange={(value) => handleInputChange(field.name, value)}
                    />
                  );
                }

                if (field.type === "phone") {
                  return (
                    <div key={field.id} className="flex flex-col space-y-1">
                      <label
                        className={twMerge(
                          "text-sm font-semibold transition-colors",
                          theme === "dark" ? "text-gray-300" : "",
                        )}
                      >
                        {field.label}
                        {field.required && (
                          <span className="text-red-500">*</span>
                        )}
                      </label>
                      <PhoneInput
                        country={"us"}
                        value={value}
                        onChange={(val, countryData: any) => {
                          const dialCode = countryData.dialCode || "";
                          const fullNumber = val.startsWith("+")
                            ? val
                            : `+${dialCode}${val.replace(dialCode, "")}`;
                          handleInputChange(field.name, fullNumber);
                        }}
                        inputProps={{
                          name: field.name,
                          required: field.required || false,
                          autoFocus: false,
                        }}
                        containerClass="w-full"
                        inputClass={twMerge(
                          "!w-full !py-2 !pl-12 !border !rounded-md transition-colors",
                          theme === "dark"
                            ? "!bg-[#191919] !border-gray-700 !text-gray-100"
                            : "!bg-white !border-gray-300 !",
                        )}
                        buttonClass={twMerge(
                          "transition-colors",
                          theme === "dark"
                            ? "!bg-[#191919] !border-gray-700"
                            : "!bg-white !border-gray-300",
                        )}
                      />
                    </div>
                  );
                }

                if (field.type === "radio") {
                  return (
                    <div key={field.id} className="flex flex-col">
                      <label
                        className={twMerge(
                          "text-sm font-medium transition-colors",
                          theme === "dark" ? "text-gray-300" : "",
                        )}
                      >
                        {field.label}
                        {field.required && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </label>
                      <div className="flex items-center gap-4 mt-1">
                        <label
                          className={twMerge(
                            "flex items-center gap-1 transition-colors",
                            theme === "dark" ? "text-gray-300" : "",
                          )}
                        >
                          <input
                            type="radio"
                            name={field.name}
                            value="Yes"
                            checked={value === "Yes"}
                            onChange={(e) =>
                              handleInputChange(field.name, e.target.value)
                            }
                          />
                          <span>Yes</span>
                        </label>
                        <label
                          className={twMerge(
                            "flex items-center gap-1 transition-colors",
                            theme === "dark" ? "text-gray-300" : "",
                          )}
                        >
                          <input
                            type="radio"
                            name={field.name}
                            value="No"
                            checked={value === "No"}
                            onChange={(e) =>
                              handleInputChange(field.name, e.target.value)
                            }
                          />
                          <span>No</span>
                        </label>
                      </div>
                    </div>
                  );
                }

                return null; // fallback if type is unknown
              })}
        </div>
        <Divider />
        {/* Department Dropdown */}
        <div className="space-y-3">
          {timeline?.department && (
            <Input
              value={formData.department || ""}
              label={t("visitors.editTimeline.selectedDepartment")}
              readonly
            />
          )}
          {timeline?.employees && (
            <Input
              value={formData.employee || "Empty"}
              label={t("visitors.editTimeline.selectedEmployee")}
              readonly
            />
          )}

          {agreementId && (
            <button
              className="flex items-center gap-2 text-[#099ed1] py-2"
              onClick={() => handleDownloadAgreement(agreementId)}
            >
              <Download />
              {t("visitors.editTimeline.downloadAgreement")}
            </button>
          )}
          <div className="space-y-1">
            <label
              className={twMerge(
                "block text-sm font-medium transition-colors",
                theme === "dark" ? "text-gray-300" : "",
              )}
            >
              {t("visitors.editTimeline.yourNotes")}
            </label>
            <textarea
              placeholder={t("visitors.editTimeline.startTyping")}
              value={notes}
              className={twMerge(
                "px-3 py-2 border rounded-md w-full h-30 transition-colors focus:outline-none focus:ring-1 focus:ring-[#099ed1]",
                theme === "dark"
                  ? "bg-[#191919] border-gray-700 text-gray-100 placeholder-gray-600"
                  : "bg-white border-gray-300 ",
              )}
              onChange={(e) => {
                setNotes(e.target.value);
                setIsChanged(true);
              }}
            />
          </div>

          {timeline?.img && (
            <div
              className={twMerge(
                "relative w-full h-80 rounded-xl overflow-hidden shadow-md flex items-center justify-center transition-colors",
                theme === "dark"
                  ? "bg-[#191919] border border-gray-800"
                  : "bg-white border border-gray-200",
              )}
            >
              <img
                src={timeline?.img}
                alt={"Preview"}
                className="object-cover"
              />
              <button
                onClick={() => {
                  window.open(timeline.img || "", "_blank");
                }}
                className={twMerge(
                  "absolute top-2 right-2 p-2 rounded-full shadow transition-all",
                  theme === "dark"
                    ? "bg-gray-800 hover:bg-gray-700 text-gray-300"
                    : "bg-white hover:bg-gray-100 text-[#099ed1]",
                )}
                title="Download"
              >
                <Download className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 pt-4">
          <Button variant="outline" onClick={onClose}>
            {t("visitors.editTimeline.cancel")}
          </Button>
          <Button variant="primary" disabled={!isChanged} onClick={handleSave}>
            {t("visitors.editTimeline.saveChanges")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EditTimeline;
